// A link that no longer opens — RON-5465, Phase A. Spec: RON-4831 §1.1, AC-P-74.
//
// `POST /visitor/portal/open` answers `state: "expired"` and the screen shows THIS AND NOTHING
// ELSE: when the link closed, and who to ask for a new one. No company name, no contractor name,
// no pack — the server does not send them, and that is deliberate.
//
// WHY SO LITTLE. "Closed" covers three different things — the link lapsed, a newer link replaced
// it, or the contractor was archived — and the server does not say which. Whoever is holding a
// stale link may not be the contractor: links travel in forwarded mail and shared inboxes. So
// the page tells them enough to act (ask this person for a new one) and nothing about the pack.
//
// THE 404 CASE USES THIS SCREEN TOO, with no date and no contact: an unknown token answers only
// "This link is not valid.". One screen rather than two, because from the reader's side the
// situation is identical — the link in their hand does not work — and a second layout would be
// two things to keep honest for no gain.

import { useTranslation } from "react-i18next";

import { formatLongDate } from "../portalConstants.js";

const PortalExpired = ({ logoUrl, closedAt, timezone, contact, message }) => {
  const { t } = useTranslation("common");

  return (
    <div className="portal-expired">
      {/* The one thing added to this reply on 24-October. It names no company — `alt` is empty
          and the image is decorative — because this screen still must not say whose pack it was
          (RON-4831 §1.1), and whoever holds a stale link may not be the contractor.

          Showing it at all is defensible because the customer sent them this link under their own
          branding in the first place; a logo tells a stranger nothing the email did not. */}
      {logoUrl && (
        <img
          className="portal-expired__logo"
          src={logoUrl}
          alt=""
          aria-hidden="true"
        />
      )}
      <span className="portal-expired__icon" aria-hidden="true">
        <svg
          width="32"
          height="32"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
      </span>

      <h1 className="portal-expired__title">{t("react_portal_expired_title")}</h1>

      {/* The date, when there is one. A 404 has none — and inventing "recently" would be worse
          than saying nothing, because the contractor may be holding a link from last year. */}
      {closedAt && (
        <p className="portal-expired__when">
          {t("react_portal_expired_when")
            .replace("%d", formatLongDate(String(closedAt).slice(0, 10)))
            // Named rather than implied. A contractor in Spain reading a Dublin deadline is
            // exactly the confusion the server sends `timezone` to prevent.
            .replace("%z", timezone || "")}
        </p>
      )}

      {/* Who to ask. `contact` is null for a link the system sent itself — renewals — so the
          fallback has to work without a name: there is genuinely nobody to point at. */}
      <p className="portal-expired__contact">
        {contact?.name
          ? t("react_portal_expired_contact")
              .replace("%s", contact.name)
              .replace("%e", contact.email || "")
          : t("react_portal_expired_contact_unknown")}
      </p>

      {/* The server's own sentence, when it gave one — a 404 says "This link is not valid." and
          that is better than our paraphrase of it. */}
      {message && <p className="portal-expired__note">{message}</p>}
    </div>
  );
};

export default PortalExpired;
